import { z } from 'zod';
import i18n from 'i18n';

/**
 * Localizing zod validation messages without rewriting 500 schemas.
 *
 * The problem: schemas are evaluated at MODULE SCOPE, not at render. A
 * `t('validation.required')` written inside a schema would resolve once, at
 * import time, in whatever language happened to be active — and would never
 * update when the user switches language.
 *
 * The solution has two halves that compose:
 *
 *   (a) A global zod error map, installed here. It covers every constraint
 *       that carries NO explicit message (`z.number()` handed a string,
 *       `z.string().min(1)` with nothing supplied) for free, with zero schema
 *       edits — which is the bulk of them.
 *
 *   (b) `resolveValidationMessage`, applied by the two components that render
 *       field errors. A schema opts in by carrying a KEY instead of prose:
 *
 *           z.string().min(1, 'validation.required')
 *           z.string().max(70, 'validation.maxLength|{"max":70}')
 *
 *       A message that is not a known key falls through UNCHANGED. That is
 *       what makes the migration incremental: an unconverted schema keeps
 *       rendering its English prose while a converted one renders Spanish,
 *       with no flag day and no coordinated release.
 *
 * The key is locale-independent, so evaluating it at module scope is fine —
 * which is the whole trick.
 */

const VALIDATION_PREFIX = 'validation.';

/**
 * Resolve a zod message that may be a translation key.
 *
 * @param {unknown} message  Whatever the schema put on the issue.
 * @param {Function} [t]     Bound `t`; defaults to the global instance so
 *                           non-React callers work.
 * @returns {unknown} The translated string, or the input untouched.
 */
export function resolveValidationMessage(message, t = i18n.t.bind(i18n)) {
  if (typeof message !== 'string' || !message.startsWith(VALIDATION_PREFIX)) return message;

  // `key|{"max":70}` — interpolation values travel with the key because a
  // schema has no other way to pass them through zod.
  const separator = message.indexOf('|');
  if (separator === -1) return t(message);

  const key = message.slice(0, separator);
  const raw = message.slice(separator + 1);
  try {
    return t(key, JSON.parse(raw));
  } catch {
    // Malformed payload: render the key's message without interpolation
    // rather than showing the user a JSON fragment.
    return t(key);
  }
}

// t('validation.required', 'Required field')
// t('validation.invalidType', 'Enter a valid value')
// t('validation.invalidEmail', 'Enter a valid email address')
// t('validation.invalidUrl', 'Enter a valid URL')
// t('validation.tooShort', 'Must be at least {{min}} characters')
// t('validation.tooSmall', 'Must be at least {{min}}')
// t('validation.maxLength', 'This field cannot exceed {{max}} characters')
// t('validation.tooLarge', 'Must be at most {{max}}')
// t('validation.notANumber', 'Enter a number')
// t('validation.invalid', 'Invalid value')
// t('validation.atLeastOne', 'At least one is required')
// t('validation.nonNegative', 'Must be non-negative')
// t('validation.cnCodeMinLength', 'CN Code must be at least 8 characters')
// t('validation.invalidNumber', 'Invalid number')
// t('validation.positive', 'Must be a positive number')
// t('validation.invalidPhone', 'Invalid phone number')
// t('validation.between', 'Must be between {{min}} and {{max}}')
// t('validation.notZero', 'Value cannot be 0')
// t('validation.selectDate', 'Please select a date')
// t('validation.selectUnit', 'Select a valid unit')
// t('validation.countryIso2', 'Country must be in ISO2 format (e.g. US, DE)')
// t('validation.maxDecimals', 'Maximum {{max}} decimal places allowed')
// t('validation.notNA', 'Cannot be N/A')
// t('validation.urlOrBlank', 'Enter a valid http(s) URL (or leave it blank)')
// t('validation.emailOrBlank', 'Enter a valid email address (or leave it blank)')
// t('validation.passwordOrOff', 'Set a password or turn password protection off')
// t('validation.linkNameTaken', 'That link name is already taken. Try a different one.')

/**
 * Install the global zod error map. Called once, from the app entry.
 *
 * Covers only issues with no explicit message — zod calls the map exactly
 * when a schema did not supply one, so this can never override a deliberate
 * per-field message.
 */
export function installZodErrorMap() {
  const t = (key, defaultValue, options) => i18n.t(key, defaultValue, options);

  z.setErrorMap((issue, ctx) => {
    switch (issue.code) {
      case z.ZodIssueCode.invalid_type:
        if (issue.received === 'undefined' || issue.received === 'null') {
          return { message: t('validation.required', 'Required field') };
        }
        if (issue.expected === 'number') {
          return { message: t('validation.notANumber', 'Enter a number') };
        }
        return { message: t('validation.invalidType', 'Enter a valid value') };

      case z.ZodIssueCode.too_small:
        // A min of 1 on a string is the idiomatic "required" in this codebase,
        // not a length constraint — say so rather than "at least 1 character".
        if (issue.type === 'string' && Number(issue.minimum) <= 1) {
          return { message: t('validation.required', 'Required field') };
        }
        if (issue.type === 'string') {
          return {
            message: t('validation.tooShort', 'Must be at least {{min}} characters', {
              min: issue.minimum,
            }),
          };
        }
        return {
          message: t('validation.tooSmall', 'Must be at least {{min}}', { min: issue.minimum }),
        };

      case z.ZodIssueCode.too_big:
        if (issue.type === 'string') {
          return {
            message: t('validation.maxLength', 'This field cannot exceed {{max}} characters', {
              max: issue.maximum,
            }),
          };
        }
        return {
          message: t('validation.tooLarge', 'Must be at most {{max}}', { max: issue.maximum }),
        };

      case z.ZodIssueCode.invalid_string:
        if (issue.validation === 'email') {
          return { message: t('validation.invalidEmail', 'Enter a valid email address') };
        }
        if (issue.validation === 'url') {
          return { message: t('validation.invalidUrl', 'Enter a valid URL') };
        }
        return { message: t('validation.invalid', 'Invalid value') };

      default:
        // Fall through to zod's own English default rather than inventing
        // copy for an issue code we have not thought about.
        return { message: ctx.defaultError };
    }
  });
}

/**
 * Message keys for use inside schemas. Prefer these over free prose so the
 * message is translatable without the schema having to become a hook.
 */
export const V = Object.freeze({
  required: `${VALIDATION_PREFIX}required`,
  invalid: `${VALIDATION_PREFIX}invalid`,
  notANumber: `${VALIDATION_PREFIX}notANumber`,
  invalidEmail: `${VALIDATION_PREFIX}invalidEmail`,
  invalidUrl: `${VALIDATION_PREFIX}invalidUrl`,
  maxLength: (max) => `${VALIDATION_PREFIX}maxLength|${JSON.stringify({ max })}`,
  tooShort: (min) => `${VALIDATION_PREFIX}tooShort|${JSON.stringify({ min })}`,
  tooSmall: (min) => `${VALIDATION_PREFIX}tooSmall|${JSON.stringify({ min })}`,
  tooLarge: (max) => `${VALIDATION_PREFIX}tooLarge|${JSON.stringify({ max })}`,
  // Specific messages kept (per the hybrid decision) where the generic wording
  // would lose real information. Reused across sites.
  atLeastOne: `${VALIDATION_PREFIX}atLeastOne`,
  nonNegative: `${VALIDATION_PREFIX}nonNegative`,
  cnCodeMinLength: `${VALIDATION_PREFIX}cnCodeMinLength`,
  invalidNumber: `${VALIDATION_PREFIX}invalidNumber`,
  positive: `${VALIDATION_PREFIX}positive`,
  invalidPhone: `${VALIDATION_PREFIX}invalidPhone`,
  between: (min, max) => `${VALIDATION_PREFIX}between|${JSON.stringify({ min, max })}`,
  notZero: `${VALIDATION_PREFIX}notZero`,
  selectDate: `${VALIDATION_PREFIX}selectDate`,
  selectUnit: `${VALIDATION_PREFIX}selectUnit`,
  countryIso2: `${VALIDATION_PREFIX}countryIso2`,
  maxDecimals: (max) => `${VALIDATION_PREFIX}maxDecimals|${JSON.stringify({ max })}`,
  notNA: `${VALIDATION_PREFIX}notNA`,
  urlOrBlank: `${VALIDATION_PREFIX}urlOrBlank`,
  emailOrBlank: `${VALIDATION_PREFIX}emailOrBlank`,
  passwordOrOff: `${VALIDATION_PREFIX}passwordOrOff`,
  linkNameTaken: `${VALIDATION_PREFIX}linkNameTaken`,
});
